import requester from "api/requester"
import {
  IWeeklyChartDataItem,
  IWeeklyOverviewPayload,
  IWeeklyOverviewResponse,
  IWeeklyWaterOverview,
} from "./types"
import dayjs from "dayjs"
import {
  DATE_PARAM_FORMAT,
  US_DATE_FORMAT_WITH_YEAR,
  US_DATE_FORMAT_WITHOUT_YEAR,
} from "config"

function parseWeeklyWaterResponse(
  data: Array<{ amount: number; date: string }>,
  start: string,
): IWeeklyWaterOverview {
  const weeklyChartData = Array.from({ length: 7 }, (_, i) => {
    const dateFormat = dayjs(start).add(i, "day").format(DATE_PARAM_FORMAT)
    const dayData = data.find((item) => item.date === dateFormat)

    return {
      name: dayjs(dateFormat).format(US_DATE_FORMAT_WITHOUT_YEAR),
      date: dayjs(dateFormat).format(US_DATE_FORMAT_WITH_YEAR),
      amount: dayData?.amount ?? 0,
    }
  })

  const weeklyTotal = weeklyChartData.reduce((acc, item) => acc + item.amount, 0)
  const daysWithData = weeklyChartData.filter((d) => d.amount > 0).length
  const avgDaily =
    daysWithData > 0 ? Math.round(weeklyTotal / daysWithData) : 0

  const waterHistory = [...weeklyChartData].reverse().map((item) => ({
    date: item.date,
    amount: item.amount,
  }))

  return { weeklyTotal, avgDaily, weeklyChartData, waterHistory }
}

const overviewAPI = {
  getWeeklyOverview(
    { start, end }: IWeeklyOverviewPayload,
    signal: AbortSignal,
  ): Promise<IWeeklyOverviewResponse> {
    return requester
      .get("/progresses/weekly", { from_date: start, to_date: end }, { signal })
      .then((data) => {
        const weeklyChartData = Array.from({ length: 7 }, (_, i) => {
          const dateFormat = dayjs(start)
            .add(i, "day")
            .format(DATE_PARAM_FORMAT)

          const day = data.daysInWeek.find(
            ({ date }: { date: string }) => date === dateFormat,
          )
          const dateMealTypeBalanceItem = day?.mealTypeBalance
            ? {
                ...day.mealTypeBalance,
                goalCalories: day.goal.caloriePerDay,
              }
            : {
                breakfast: 0,
                lunch: 0,
                dinner: 0,
                snack: 0,
                goalCalories: 0,
              }

          const name = dayjs(dateFormat).format(US_DATE_FORMAT_WITHOUT_YEAR)
          const date = dayjs(dateFormat).format(US_DATE_FORMAT_WITH_YEAR)

          return {
            ...dateMealTypeBalanceItem,
            name,
            date,
          } as IWeeklyChartDataItem
        })

        return {
          mealTypeBalance: data.mealTypeBalance,
          gainedCalories: data.calorie,
          mostCalories: data.mostCalorie,
          mostProtein: data.mostProtein,
          mostCarbs: data.mostCarb,
          mostFat: data.mostFat,
          weeklyChartData,
        }
      })
  },

  getWeeklyWater(
    { start, end }: IWeeklyOverviewPayload,
    signal: AbortSignal,
  ): Promise<IWeeklyWaterOverview> {
    return requester
      .get("/water-tracking/weekly", { from_date: start, to_date: end }, { signal })
      .then((data) => parseWeeklyWaterResponse(data, start))
  },
}

export default overviewAPI
